/**
 * Shared display primitives for the operator responsibility (Betreiberverantwortung)
 * curation surface.
 *
 * Liability posture: confirmed vs. pending vs. synthesized must be visually
 * distinct EVERYWHERE they appear — unconfirmed LLM output is never styled
 * as authoritative. These badges are the single source of that styling.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

const badgeStyle = (background: string, color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: "10px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  backgroundColor: background,
  color,
});

const REVIEW_STATUS_COLORS: Record<string, { background: string; color: string }> = {
  pending: { background: "#fff4ce", color: "#8a6d00" },
  legalConfirmed: { background: "#e6f2fb", color: "#0b5a8f" },
  confirmed: { background: "#dff6dd", color: "#0b6a0b" },
  rejected: { background: "#fde7e9", color: "#a4262c" },
  needsRevision: { background: "#fcefe3", color: "#8f4e00" },
};

export const ReviewStatusBadge = ({ status }: { status?: string }) => {
  const { t } = useTranslation();
  const colors = REVIEW_STATUS_COLORS[status || ""] || { background: "#f3f2f1", color: "#444" };

  return (
    <span style={badgeStyle(colors.background, colors.color)}>
      {t(`operatorResponsibilityPanel.reviewStatus.${status || "unknown"}`)}
    </span>
  );
};

/**
 * Review status with the legal effective date as a second line. The two are
 * independent dimensions (bitemporal pair): validFrom states when the
 * obligation is legally effective, the badge states what the review says
 * about it. Pending-but-already-effective is emphasized — that combination
 * must never read as "confirmed/current".
 */
export const ReviewStatusWithEffectiveDate = ({ obligation }: { obligation: any }) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        alignItems: "flex-start",
        textAlign: "left",
      }}
    >
      <ReviewStatusBadge status={obligation.reviewStatus} />
      {obligation.validFrom ? (
        <span
          style={{
            // align with the badge label text (the badge has 8px horizontal padding)
            paddingLeft: "8px",
            fontSize: "11px",
            color: isEffectiveButPending(obligation) ? "#8a6d00" : "#666",
            fontWeight: isEffectiveButPending(obligation) ? 600 : 400,
          }}
        >
          {t("operatorResponsibilityPanel.effectiveSince", { date: formatDate(obligation.validFrom) })}
        </span>
      ) : null}
    </div>
  );
};

export const ProvenanceBadge = ({
  provenanceType,
  generatedBy,
}: {
  provenanceType?: string;
  generatedBy?: { model?: string; promptVersion?: string };
}) => {
  const { t } = useTranslation();
  const colors =
    provenanceType === "synthesized"
      ? { background: "#efe5fb", color: "#5b2e91" }
      : { background: "#deecf9", color: "#004578" };

  return (
    // The concrete model stays in the data + tooltip (audit trail); the badge
    // itself only says "AI" — no product names in the UI.
    <span
      style={badgeStyle(colors.background, colors.color)}
      title={generatedBy?.model ? `${generatedBy.model}${generatedBy.promptVersion ? ` (${generatedBy.promptVersion})` : ""}` : undefined}
    >
      {t(`operatorResponsibilityPanel.provenance.${provenanceType || "unknown"}`)}
      {generatedBy?.model ? ` · ${t("operatorResponsibilityPanel.provenance.ai")}` : ""}
    </span>
  );
};

export const CoverageStatusBadge = ({ status }: { status?: string }) => {
  const { t } = useTranslation();
  const colors =
    status === "covered"
      ? { background: "#dff6dd", color: "#0b6a0b" }
      : status === "modelBaseline"
        ? { background: "#deecf9", color: "#004578" }
        : status === "pending"
          ? { background: "#fff4ce", color: "#8a6d00" }
          : { background: "#fde7e9", color: "#a4262c" };

  return (
    <span style={badgeStyle(colors.background, colors.color)}>
      {t(`operatorResponsibilityPanel.coverageStatus.${status || "unknown"}`)}
    </span>
  );
};

export const ConfidenceIndicator = ({ value }: { value?: number }) => {
  if (typeof value !== "number") {
    return <span>—</span>;
  }

  const color = value >= 0.9 ? "#0b6a0b" : value >= 0.8 ? "#8a6d00" : "#a4262c";

  return <span style={{ color, fontWeight: 600 }}>{Math.round(value * 100)}%</span>;
};

/**
 * Width-capped, wrapping table-cell text. The DataTable shim renders a
 * table-layout:auto table, so long no-wrap content forces its column wide —
 * a maxWidth on the block content is what actually caps the column. The
 * full text stays visible by wrapping (the shim's default cell style is
 * nowrap, so wrapping must be set explicitly here).
 */
export const WrappedText = ({
  text,
  maxWidth,
  fontSize,
}: {
  text?: string;
  maxWidth: number;
  fontSize?: number;
}) => (
  <span
    style={{
      display: "block",
      maxWidth: `${maxWidth}px`,
      whiteSpace: "normal",
      overflowWrap: "break-word",
      ...(fontSize ? { fontSize: `${fontSize}px` } : {}),
    }}
  >
    {text || "—"}
  </span>
);

/**
 * Localized display name for a regulated-object code (the controlled
 * vocabulary bridge keys, e.g. 'aufzugsanlage'). Falls back to the
 * vocabulary's own `name` (or the raw code) for codes without a translation —
 * the vocabulary is expert-extensible, the i18n map is not exhaustive.
 */
export const regulatedObjectName = (
  // i18next's TFunction returns string | TFunctionDetailedResult — accept any
  // t-shaped function and normalize to string.
  t: (key: string, options?: any) => any,
  code?: string,
  fallback?: string,
): string =>
  code
    ? String(t(`operatorResponsibilityPanel.regulatedObjectValue.${code}`, { defaultValue: fallback || code }))
    : "—";

/**
 * Anzeigename einer Subjektklasse (zweite Achse), gleiche Mechanik wie `regulatedObjectName`.
 */
export const regulatedSubjectName = (
  t: (key: string, options?: any) => any,
  code?: string,
): string =>
  code
    ? String(t(`operatorResponsibilityPanel.regulatedSubjectValue.${code}`, { defaultValue: code }))
    : "—";

/**
 * Woran die Pflicht hängt — gleich welche der beiden Achsen sie trägt.
 *
 * Listen und Filter dürfen die Achsen nicht auseinanderreißen: sonst verschwinden die
 * Subjektpflichten aus jeder anlagenorganisierten Ansicht, und zwar nicht als "0 Treffer",
 * sondern als nie erwogene Kandidaten.
 */
export const regulatedRefName = (
  t: (key: string, options?: any) => any,
  obligation: { regulatedObjectCode?: string | null; regulatedSubject?: string | null },
  fallback?: string,
): string =>
  obligation?.regulatedSubject
    ? regulatedSubjectName(t, obligation.regulatedSubject)
    : regulatedObjectName(t, obligation?.regulatedObjectCode || undefined, fallback);

/** Localized label for an obligation field key (e.g. 'modality' → 'Modalität'). */
export const obligationFieldLabel = (t: (key: string, options?: any) => any, field: string): string =>
  String(
    t(`operatorResponsibilityPanel.${field === "inspection" ? "inspectionSummary" : field}`, {
      defaultValue: field,
    }),
  );

/** LEGAL-12 qualification role ids (tenant-neutral master roles). */
export const QUALIFICATION_ROLE_IDS = [
  "competentPerson",
  "approvedInspectionBody",
  "qualifiedElectrician",
  "expert",
  "certifiedPerson",
  "instructedPerson",
  "sachkundigePerson",
];

/**
 * Resolve a checklist role reference for display. Law-derived masters store
 * the STABLE qualification role id (LEGAL-12) so renames don't break the
 * reference; legacy checklists still carry free-text names — those pass
 * through unchanged.
 */
export const qualificationRoleName = (
  t: (key: string, options?: any) => any,
  role?: any,
): string => {
  if (!role) {
    return "—";
  }
  const value = typeof role === "string" ? role : role.name || role.id || "";
  return QUALIFICATION_ROLE_IDS.includes(value)
    ? String(t(`operatorResponsibilityPanel.qualificationRole.${value}`))
    : String(value);
};

export const formatDate = (value: any): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

export const formatDateTime = (value: any): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

export const isEffectiveButPending = (obligation: any): boolean =>
  obligation &&
  (obligation.reviewStatus === "pending" || obligation.reviewStatus === "legalConfirmed") &&
  !obligation.validTo &&
  obligation.validFrom &&
  new Date(obligation.validFrom).getTime() <= Date.now();

/**
 * "Change effective from <date>, review pending" — an obligation whose legal
 * effective date has passed but which is still unreviewed must NEVER look
 * confirmed/current. After the Recht approval it says so: the second approval
 * is what is missing, not the review as such.
 *
 * `compact` is the one-line form for list rows, where the full banner repeated
 * on every row crowded out the obligation itself.
 */
export const EffectivePendingWarning = ({ obligation, compact = false }: { obligation: any; compact?: boolean }) => {
  const { t } = useTranslation();

  if (!isEffectiveButPending(obligation)) {
    return null;
  }

  const stage = obligation.reviewStatus === "legalConfirmed" ? "legalConfirmed" : "pending";
  const date = formatDate(obligation.validFrom);

  if (compact) {
    return (
      <span className="min-w-0 break-words text-xs font-medium text-warning">
        {t(`operatorResponsibilityPanel.effectiveButPendingShort.${stage}`, { date })}
      </span>
    );
  }

  return (
    <div
      role="status"
      className="flex min-w-0 items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm font-semibold text-warning"
    >
      <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0 break-words">
        {t(`operatorResponsibilityPanel.effectiveButPending.${stage}`, { date })}
      </span>
    </div>
  );
};
